/**
 * .no-main--why — 스크롤 스택 카드 (레퍼런스 .cards 패턴).
 * `.no-main { overflow: hidden }` 때문에 CSS sticky 가 동작하지 않으므로
 * GSAP ScrollTrigger `pin` 으로 구현한다.
 *
 * 섹션 전체(타이틀+스택)를 top top 으로 핀 고정한다. 스택은 카드 한 장 높이라
 * 카드들이 같은 박스에 포개지고, 타이틀은 덱 바로 위에 함께 머무른다.
 * (centered max-width 자식을 position:fixed 로 핀하면 Lenis 프록시와 어긋나
 *  핀이 안 잡혀 스크럽이 멈추므로, 풀폭 섹션을 핀하는 .about-blocks 패턴을 따른다.)
 * 1번 카드는 그대로 둔 채 2번부터 아래(yPercent:75)+투명에서 올라와 이전 카드를 덮고,
 * 뒤로 깔리는 카드는 scale 축소 + 위로 이동해 덱 가장자리가 드러나도록 겹친다.
 *
 * 초기 숨김(2번 이후 opacity/translateY)은 SCSS 가 소유(.js 게이트).
 * GSAP 부재 시에만 is-why-fallback 으로 세로 흐름 폴백.
 */
export function initWhyStack() {
  const section = document.querySelector(".no-main--why");
  if (!section) {
    return;
  }

  const inner = section.querySelector(".no-main--why__inner");
  const stack = section.querySelector("[data-why-stack]");
  const cards = Array.from(section.querySelectorAll("[data-why-card]"));
  if (!inner || !stack || cards.length < 2) {
    return;
  }

  const ready =
    typeof window.gsap !== "undefined" &&
    typeof window.ScrollTrigger !== "undefined";

  // GSAP 미로드 시에만 일반 흐름(세로 카드 리스트)으로 폴백.
  if (!ready) {
    section.classList.add("is-why-fallback");
    return;
  }

  const tl = gsap.timeline({
    defaults: { ease: "none" },
    scrollTrigger: {
      trigger: section,
      pin: true,
      pinSpacing: true,
      // 섹션(타이틀+스택)을 통째로 핀 → 타이틀이 덱과 함께 머무른다.
      start: "top top",
      // 카드당 스크롤 여유를 늘려 한 장 한 장 천천히·부드럽게 안착하게 한다.
      end: "+=" + cards.length * 700,
      scrub: 1.2, // 스크롤 따라잡기를 살짝 늦춰 끌리는 느낌 완화
      // SCSS 에서 섹션을 한 뷰포트 높이(100svh)로 맞췄으므로(.about-blocks 패턴)
      // top top 핀이 매끄럽게 잡힌다. 동작하는 .about-blocks 와 동일하게 anticipatePin 사용.
      anticipatePin: 1,
      invalidateOnRefresh: true,
    },
  });

  // 2번 카드부터: 아래(75%)+투명 → 제자리로 상승하며 앞 카드를 덮는다.
  // 동시에 뒤로 깔리는 카드들은 한 단계씩 축소되어 가운데에서 포개진다.
  for (let i = 1; i < cards.length; i++) {
    // 위치는 1구간에 걸쳐 끌어올리되, opacity 는 초반(0.18)에 빠르게 1로 채워
    // 올라오는 동안 반투명하게 비치지 않게 한다.
    tl.fromTo(
      cards[i],
      { yPercent: 75 },
      { yPercent: 0, duration: 1, ease: "power2.out" }, // 빠르게 올라와 부드럽게 감속·안착
    );
    tl.fromTo(
      cards[i],
      { opacity: 0 },
      { opacity: 1, duration: 0.18 },
      "<", // 상승 시작과 동시에 빠르게 불투명
    );

    for (let j = 0; j < i; j++) {
      const depth = i - j; // 1 = 바로 뒤, 클수록 더 깊음
      tl.to(
        cards[j],
        {
          scale: 1 - depth * 0.04,
          yPercent: -depth * 5, // 뒤 카드를 위로 더 끌어올려 덱(겹침) 가장자리가 보이게
          duration: 1,
          ease: "power2.out", // 올라오는 카드와 같은 감속 곡선으로 함께 부드럽게
        },
        "<", // 새 카드가 올라오는 것과 동시에
      );
    }
  }

  // 핀 측정/refresh 는 app.js 가 모든 인터랙션·리빌 생성 "뒤"에 전역으로 한 번
  // 수행한다(레이아웃 정착 후). 여기서 별도 refresh 하면 정착 전 spacer 가
  // 먼저 계산됐다가 전역 refresh 로 다시 잡히며 높이가 튀므로 호출하지 않는다.
}
